/* eslint-disable @typescript-eslint/no-explicit-any */
// Account - Favourites Page -  Auth Page

import { GetServerSideProps } from 'next';
import { ApolloProvider } from '@apollo/client';
import { inspect } from 'util';

import { ResponsiveGrid } from '@adobe/aem-react-editable-components';

import { respGridUtil, getProcessEnvs, prePageCall, Layout, logger } from '@marriott/mi-headless-utils';
import { FEATURE_FLAGS, TARGETS } from '@marriott/mi-feature-flag-utils';
import { getDeployedEnvType, useMiApolloClient } from '@marriott/mi-apollo-client-utils';

import { constants } from '@marriott/mi-account-components/constants';

import {
  preProcessorCall,
  getLocaleFromParams,
  isACDLEnabled,
  fetchFlags,
} from '@marriott/mi-account-components/utils';

// update this for forgot password
import { ForgotPasswordChallengeModel } from '../../_localDevMocks';
import operationSignatures from '../../_constants/operationSignatures.json';

const { NEXT_PUBLIC_AEM_PATH, SKIP_PREPROCESSOR_CALL, IS_LOCAL_DEV, DISABLE_EAA_DYNAMIC_LOCALE } = getProcessEnvs();
const { ITEM_PATH_ROOT, UNAUTH_URL, NEXT_PUBLIC_ENV_KEYS } = constants;

global.loggerInstance = logger({}) as CallableFunction;
const { log } = global.loggerInstance('ForgotPassword');

export default function ForgotPasswordPage({ model, deployedEnvType }) {
  const MiApolloClient = useMiApolloClient(operationSignatures, deployedEnvType);

  const {
    metaNames,
    metaProperties,
    title,
    staticComponentClientLibsCSS,
    staticComponentClientLibsJS,
    gridCss,
    authorMode,
    canonical,
  } = model;
  const responsiveGridModel = respGridUtil(model?.cqItems?.root?.[':items']?.responsivegrid);

  return (
    <ApolloProvider client={MiApolloClient}>
      <Layout
        metaNames={metaNames}
        metaProps={metaProperties}
        title={title}
        staticComponentClientLibsCSS={staticComponentClientLibsCSS}
        staticComponentClientLibsJS={staticComponentClientLibsJS}
        gridCss={gridCss}
        isAuthorMode={authorMode}
        canonical={canonical}
      >
        <ResponsiveGrid
          {...responsiveGridModel}
          model={responsiveGridModel}
          pagePath={NEXT_PUBLIC_AEM_PATH}
          itemPath={ITEM_PATH_ROOT}
          customClassName="account-page-wrapper"
        />
      </Layout>
    </ApolloProvider>
  );
}

//Any product specific page data apart from common data like session, datalayer
//can be returned from this function and the same is passed as props to the page Component
const ForgotPasswordPageSpecificData = async request => {
  const deployedEnvType = getDeployedEnvType();
  let response: any = { operationSignatures: [], deployedEnvType, isEAA: false };
  const resolvedUrlString: string = request?.req?.url;
  const isAuthorMode = resolvedUrlString?.includes('isAuthorMode');
  const urlQueryParameters = new URLSearchParams(request?.query)?.toString();
  const preprocessorPath = urlQueryParameters ? `${UNAUTH_URL}?${urlQueryParameters}` : UNAUTH_URL;
  let currentLocale;

  const featureFlags = request?.req?.headers?.iseaa
    ? await fetchFlags(
        [
          FEATURE_FLAGS.CNEXPERIENCE_CNMCOM_EAA_PAYLOAD_ENCRYPT,
          FEATURE_FLAGS.EXPERIENCE_MCOM_ACCOUNT_ACDL_ENABLED_PAGES_LOCALES,
        ],
        {
          target: TARGETS.APP.ACCOUNT_RENDERER,
        }
      )
    : await fetchFlags([FEATURE_FLAGS.EXPERIENCE_MCOM_ACCOUNT_FORGOT_PASSWORD_REDIRECTION], {
        target: TARGETS.APP.ACCOUNT_RENDERER,
      });

  // check if locale is present as url Param and assign it if it is found.
  // Skip param check for CN domain and pick locale from accept-language header
  if (DISABLE_EAA_DYNAMIC_LOCALE !== 'true' && resolvedUrlString?.includes('locale=')) {
    currentLocale = getLocaleFromParams(resolvedUrlString);
  }

  log.debug(`Url param locale value: ${currentLocale}`);
  /**
   * Preprocessor Call Start
   */
  /** Bypassing preprocessor call intentionally for testing. It should be reverted back */
  if (SKIP_PREPROCESSOR_CALL !== 'true' && IS_LOCAL_DEV !== 'true' && !isAuthorMode) {
    // Making page specific preprocessor call
    log.debug(`[ForgotPasswordPageSpecificData] session Id: ${request?.req?.cookies?.sessionID}`);
    const customHeaders = { isEAA: request?.req?.headers?.iseaa ?? '' };
    const preprocessorResponse = await preProcessorCall(
      request?.req?.cookies?.sessionID,
      request?.req,
      preprocessorPath,
      customHeaders
    );
    log.debug(`[ForgotPasswordPageSpecificData] PreprocessorResponse: ${inspect(preprocessorResponse)}`);
    if (preprocessorResponse?.data?.error) {
      const error = {
        redirect: { redirect: preprocessorResponse?.data?.redirect },
        value: true,
      };
      response = { operationSignatures: [], deployedEnvType, error };
    } else if (preprocessorResponse?.data) {
      response = {
        operationSignatures: [],
        deployedEnvType,
        isEAA: preprocessorResponse?.data?.isEAA,
        isMobileAuthFeatureEnabled: preprocessorResponse?.data?.isMobileAuthFeatureEnabled,
        nextPublicEnvKeys: NEXT_PUBLIC_ENV_KEYS,
      };
      if (currentLocale) {
        response = { ...response, currentLocale: currentLocale };
      }
    }
  }
  /**
   * Preprocessor Call End
   */
  return { ...response, featureFlags };
};

const postAEMModelOperations = async (
  ...args: [unknown, unknown, unknown, Record<string, unknown>, Record<string, Record<string, string>>]
) => {
  const [, , , model] = args;
  const [, , , , pageSpecificData] = args;

  const IS_CURRENT_LOCALE_AND_PAGE_ENABLED_WITH_ACDL = isACDLEnabled(
    model,
    pageSpecificData?.['featureFlags']?.['experience_mcom_account_acdl_enabled_pages_locales'],
    'forgotPassword'
  );

  return {
    ...model,
    IS_CURRENT_LOCALE_AND_PAGE_ENABLED_WITH_ACDL,
    modelJsonUpdated: false, // jjust for testing log
  };
};

//Make pre page calls like session, datalayer, fetch model.json, producSpecifc data etc
//at server side and pass the data is passed as props to page component.
export const getServerSideProps: GetServerSideProps<any> = prePageCall(
  ForgotPasswordPageSpecificData,
  ForgotPasswordChallengeModel,
  false,
  false,
  false,
  undefined,
  postAEMModelOperations
);
